import { onboardingQueueParams, type QueueEmployeeType } from "../../../../utils/onboarding-queue.ts";
import { useCallback, useEffect, useState } from "react";

import { API_ONBOARDING_QUEUE } from "../../../../../config/Endpoints.js";
import { getAuthToken } from "../../../../../utils/Storage.js";
import { ApiError, getApi } from "../../../../utils/api.ts";
import {
  parseHrSetupPagedBody,
  type HrSetupEmployeeRow,
} from "../schemas/hr-setup-employee.schema.ts";
import { useSqeLabs } from "../../../../hooks/use-sqe-labs.ts";
import { SQE_ONBOARDING, sqePageParams } from "../../onboarding/sqe/sqe-onboarding-api.ts";

function accessToken(): string | undefined {
  const t = getAuthToken("accessToken");
  return t != null && t !== "" ? t : undefined;
}

export function useHrSetupList(page: number, pageSize: number, employeeType: QueueEmployeeType = "ALL") {
  const isSqe = useSqeLabs();
  const [rows, setRows] = useState<HrSetupEmployeeRow[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(
    async (signal?: AbortSignal) => {
      setLoading(true);
      setError(null);
      const url = isSqe ? `${SQE_ONBOARDING}?${sqePageParams(page, pageSize, "L1VERIFIED")}` : `${API_ONBOARDING_QUEUE}?${onboardingQueueParams("HR", employeeType, page, pageSize)}`;
      try {
        const { data } = await getApi<unknown>(url, accessToken(), "json", signal);
        if (signal?.aborted) return;
        const parsed = parseHrSetupPagedBody(data);
        setRows(parsed.rows);
        setTotalPages(Math.max(1, parsed.totalPages));
        setTotalElements(parsed.totalElements);
      } catch (e) {
        if (signal?.aborted) return;
        const msg = e instanceof ApiError ? e.message : "Failed to load HR setup employees.";
        setError(msg);
        setRows([]);
        setTotalPages(1);
        setTotalElements(0);
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [page, pageSize, employeeType, isSqe],
  );

  useEffect(() => {
    const ac = new AbortController();
    void refetch(ac.signal);
    return () => ac.abort();
  }, [refetch]);

  return { rows, totalPages, totalElements, loading, error, refetch };
}
